import React, { useState, useEffect, useRef } from "react";
import AudioControls from "./audio-controls";
import Backdrop from "./back-drop";
import audio from './audio.mp3'
import { Box, Button, Slider, Typography } from "@material-ui/core";
import { Stack } from "@mui/material";
import { ReactComponent as Play } from "./assets/play.svg";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import { ReactComponent as Pause } from "./assets/pause.svg";
import { customTheme } from "../../styling";
import axiosInstance from "../../axios";
import { MoreVert } from "@material-ui/icons";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CircularProgress from "@material-ui/core/CircularProgress";

const tracks = [
  {
    title: "Cali",
    artist: "Audio 1 CEO.mp3",
    color: "#00aeb0"
  },
];

const AudioPlayer = ({ audioSrc, handleOpenPopover, playbackRate = 1, isRegenerating = false }) => {
  // State
  const [trackIndex, setTrackIndex] = useState(0);
  const [trackProgress, setTrackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Refs
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null)
  const intervalRef = useRef(null)
  const isReady = useRef(false);

  const duration = Number.isFinite(audioRef.current?.duration) ? audioRef.current.duration : 0;

  const currentPercentage = duration ? `${(trackProgress / duration) * 100}%` : "0%";
  const trackStyling = 'red';

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio()
    }

    const audio = audioRef.current
    audio.pause()
    audio.src = audioSrc
    audio.playbackRate = playbackRate || 1
    audio.currentTime = 0
    setTrackProgress(0)

    const handleLoadStart = () => setAudioReady(false)
    const handleCanPlay = () => setAudioReady(true)

    audio.addEventListener('loadstart', handleLoadStart)
    audio.addEventListener('canplaythrough', handleCanPlay)

    if (!isRegenerating && isReady.current) {
      audio.play().catch(() => {})
      setIsPlaying(true)
      startTimer(audio)
    } else {
      setIsPlaying(false)
    }
    isReady.current = true

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart)
      audio.removeEventListener('canplaythrough', handleCanPlay)
    }
  }, [audioSrc, playbackRate, isRegenerating])


  const startTimer = (audio = audioRef.current) => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (!audio) return;

      if (audio.ended) {
        setTrackProgress(0);
        setIsPlaying(false);
        clearInterval(intervalRef.current);
      } else {
        setTrackProgress(audio.currentTime);
      }
    }, 200);
  };

  const onScrub = (value) => {
    clearInterval(intervalRef.current);
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = value;
    setTrackProgress(a.currentTime);
  };

  // pause if regenerating starts
  useEffect(() => {
    if (isRegenerating) {
      // pause playback and prevent playing
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(intervalRef.current);
    }
  }, [isRegenerating]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate || 1;
    }
  }, [playbackRate]);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play();
      startTimer();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      audioRef.current.pause();
      clearInterval(intervalRef.current);
    };
  }, []);

  // disabled flag local
  const disabled = Boolean(isRegenerating);

  return (
    <Box style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Box
        style={{
          border: '1px solid #ccc',
          borderRadius: 15,
          width: '100%',
          padding: '0.8rem 1rem',
          display: 'flex',
          alignContent: 'center',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          transition: 'opacity 150ms ease'
        }}
        aria-disabled={disabled}
      >
        <Stack direction="row" sx={{ right: '1rem', flex: 1, display: 'flex', alignItems: 'center' }}>
          {isPlaying ? (
            <PauseRoundedIcon
              disabled={!audioReady || disabled}
              style={{ marginRight: '1rem', cursor: disabled ? 'none' : 'pointer' }}
              onClick={() => { if (!disabled) setIsPlaying(false); }}
              aria-disabled={disabled}
            />
          ) : (
            <>
              {!audioReady ? (
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    marginRight: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <PlayArrowRoundedIcon
                  disabled={!audioReady || disabled}
                  style={{ marginRight: '1rem', cursor: disabled ? 'default' : 'pointer' }}
                  onClick={() => audioReady && !disabled && setIsPlaying(true)}
                  aria-disabled={disabled}
                />
              )}
            </>
          )}

          <Slider
            step={0.1}
            min={0}
            max={duration}
            value={Math.min(trackProgress, duration)}
            onChange={(event, v) => {
              if (disabled) return;
              const newValue = Array.isArray(v) ? v[0] : v;
              onScrub(Number(newValue) || 0);
            }}
            onChangeCommitted={(event, v) => {
              if (disabled) return;
              const newValue = Array.isArray(v) ? v[0] : v;
              const n = Number(newValue) || 0;
              audioRef.current.currentTime = n;
              setTrackProgress(n);
              if (isPlaying) startTimer();
            }}
            aria-label="progress"
            disabled={!audioReady || disabled} // slider visual disabled
          />

          <EditOutlinedIcon
            style={{ float: 'right', cursor: disabled ? 'default' : 'pointer', marginLeft: '1rem' }}
            onClick={(event) => { if (!disabled) handleOpenPopover(event); }}
            aria-disabled={disabled}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default AudioPlayer;
